import { useState } from "react";
import { NavLink } from "react-router-dom";
import { trackCourseInterest, trackCtaClick, trackUserActivity } from "../services/analyticsService";
import { createEnquiry } from "../services/enquiryService";
import qaCoderLogo from "../assets/qacoder-logo.svg";
import { validateDemoEnquiryForm } from "../utils/leadValidation";
import "../style/enroll.css";

const courses = [
  {
    title: "Complete QA Career Program",
    shortTitle: "Complete QA",
    desc: "Manual Testing, Selenium, Playwright, API Testing, SQL, framework building, projects, resume help, and mock interviews.",
    duration: "4 to 5 months",
    online: "Rs. 25,000",
    offline: "Rs. 28,000",
    badge: "Best for job-ready QA career",
    popular: true,
    outcomes: ["Manual + Automation confidence", "2 real-time projects", "Interview-ready resume"],
    details:
      "This complete QA program takes learners from testing basics to real project execution. You learn Manual Testing, SDLC, STLC, Jira, SQL, Selenium with Java, Playwright, API Testing with Postman and Rest Assured, TestNG, Cucumber BDD, Git, Jenkins, Agile workflow, resume preparation, mock interviews, and job guidance.",
  },
  {
    title: "Manual Testing Foundation",
    shortTitle: "Manual Testing",
    desc: "Learn test cases, bug reporting, SDLC, STLC, Agile, Jira, test scenarios, and practical QA documentation.",
    duration: "1 month",
    online: "Rs. 8,000",
    offline: "Rs. 9,000",
    badge: "Best for beginners",
    outcomes: ["Strong QA basics", "Test case writing", "Bug reporting practice"],
    details:
      "A beginner-friendly course covering testing fundamentals, test case design, test scenarios, defect life cycle, Agile process, Jira usage, functional testing, regression testing, smoke testing, sanity testing, and practical project documentation.",
  },
  {
    title: "Selenium Automation with Java",
    shortTitle: "Selenium Java",
    desc: "Build Selenium WebDriver skills with Java, TestNG, POM, waits, reports, Maven, Git, and Jenkins.",
    duration: "1.5 months",
    online: "Rs. 10,000",
    offline: "Rs. 11,000",
    badge: "Most demanded automation skill",
    popular: true,
    outcomes: ["Selenium framework", "TestNG execution", "CI/CD basics"],
    details:
      "Learn Java for automation, Selenium WebDriver, locator strategies, waits, alerts, frames, windows, TestNG, Page Object Model, data-driven framework, reporting, Maven, GitHub, Jenkins, and automation project execution.",
  },
  {
    title: "API Testing with Postman and Rest Assured",
    shortTitle: "API Testing",
    desc: "Learn HTTP methods, authentication, JSON validation, Postman collections, and Rest Assured automation.",
    duration: "1 month",
    online: "Rs. 8,000",
    offline: "Rs. 9,000",
    badge: "Best backend testing skill",
    popular: true,
    outcomes: ["Postman confidence", "Rest Assured basics", "API automation project"],
    details:
      "This course covers API fundamentals, HTTP methods, status codes, headers, parameters, request body, authentication, OAuth basics, JSON schema validation, Postman collections, environment variables, Rest Assured, and API automation project practice.",
  },
];

const pricingPlans = [
  {
    name: "Foundation",
    price: "Rs. 8,000",
    duration: "1 month",
    idealFor: "Freshers starting from zero",
    features: ["Manual Testing", "Test cases and defects", "Jira basics", "Study notes"],
  },
  {
    name: "Career Program",
    price: "Rs. 25,000",
    duration: "4 to 5 months",
    idealFor: "Best for job preparation",
    featured: true,
    features: ["Manual + Automation", "API + SQL", "Real-time projects", "Resume and mock interviews"],
  },
  {
    name: "Automation Upgrade",
    price: "Rs. 10,000",
    duration: "1 to 1.5 months",
    idealFor: "Manual testers upgrading skills",
    features: ["Selenium or Playwright", "Framework design", "Git and Jenkins", "Project practice"],
  },
];

const learnerProfiles = [
  "Freshers who want their first QA job",
  "Non-IT graduates who need a structured entry path",
  "Manual testers who want automation skills",
  "Working professionals switching to software testing",
  "Students preparing for interviews and real projects",
  "Learners who need resume, mock interview, and job guidance",
];

const buildItems = [
  "Manual testing project with test scenarios, test cases, and defect reports",
  "Selenium automation framework using Java, TestNG, and Page Object Model",
  "API testing collection with Postman and automation basics using Rest Assured",
  "SQL testing practice set for database validation",
  "Jira-style bug tracking workflow and Agile sprint testing documentation",
  "Final resume-ready QA portfolio with project explanation points",
];

const outcomes = [
  { value: "2+", label: "Real-time projects" },
  { value: "10+", label: "Mock interview rounds" },
  { value: "25+", label: "Practical assignments" },
  { value: "1:1", label: "Resume guidance" },
];

const mentorHighlights = [
  "Lead Engineer guidance from real QA automation project experience",
  "Selenium, Playwright, Cypress, API, mobile, CI/CD, and framework practice",
  "Interview preparation with project explanation and resume-ready talking points",
  "Training style focused on real defects, test strategy, and hands-on execution",
];

const mentorTools = [
  "Selenium",
  "Playwright",
  "Cypress",
  "Postman",
  "Rest Assured",
  "Jenkins",
  "Azure DevOps",
  "Appium",
  "Jira",
  "TestNG",
];

const trainerProfiles = [
  {
    initials: "MP",
    name: "Manish Prajapati",
    role: "Founder, Trainer and Lead Engineer",
    description:
      "Leads practical QA training with hands-on automation experience across logistics, ed-tech, fintech, newsletter, music learning, and transport platforms.",
    focus: ["Lead Engineer", "Framework Design", "API Validation", "CI/CD","Java Programming", "API Testing Aptitude", "Jenkins Integration", "Rest Assured", "Agile Methodology", "Test Coverage", "Automation Proficiency", "Framework Development", "Automation Scripting", "Cypress", "Manual Test Case Preparation", "Continuous Learning", "Bug Tracking", "Mobile Applications Testing", "Manual Testing", "Selenium", "Jira", "JavaScript", "TypeScript", "Playwright", "Azure DevOps", "TestNG", "Appium", "Newman"],
  },
  {
    initials: "MK",
    name: "Mithlesh Kumar",
    role: "Co-Founder, Senior Testing Trainer and Automation Expert",
    description:
      "Co-leads learner mentoring with a focus on Selenium with Java, API testing, performance basics, CI/CD workflow, and project-based automation learning.",
    focus: ["Selenium Java", "API Testing", "Project Practice","Automation", "API Automation", "Java", "Python", "Selenium", "BDD (Cucumber)", "Hybrid Framework (POM)", "TestNG", "Appium (Mobile Automation)", "Playwright", "TDD", "Postman", "Rest Assured", "Functional Testing", "Agile Methodology", "Manual Testing", "RTM", "Test Case & Scenario", "SQL", "SDLC & STLC", "Kafka", "JMeter"],
  },
];

const successStories = [
  {
    name: "Rahul",
    result: "Fresher to QA Tester",
    company: "Infosys",
    quote: "The practical projects and interview preparation helped me explain testing confidently.",
  },
  {
    name: "Priya",
    result: "Manual to Automation",
    company: "Capgemini",
    quote: "Framework training and mock interviews helped me crack automation interview rounds.",
  },
  {
    name: "Sanchit",
    result: "Career switch to QA",
    company: "Iris",
    quote: "I understood real QA workflow through projects, API testing, and resume guidance.",
  },
];

const faqs = [
  {
    question: "Should I learn manual testing or automation first?",
    answer:
      "Start with manual testing if you are a beginner. Automation becomes easier when you understand test cases, defects, SDLC, STLC, and real QA workflow first.",
  },
  {
    question: "Can I join from a non-IT background?",
    answer:
      "Yes. The course starts from basic software testing concepts and gradually moves into tools, projects, API testing, SQL, and automation.",
  },
  {
    question: "Do you provide job support?",
    answer:
      "We provide resume help, mock interviews, interview questions, project explanation support, and guidance for applying to QA roles.",
  },
  {
    question: "Will I get a certificate?",
    answer:
      "Yes. After completing the course and assignments, learners receive a course completion certificate.",
  },
  {
    question: "Do you help with resume preparation?",
    answer:
      "Yes. Resume preparation is included in the career program, along with project points and interview-focused explanation support.",
  },
  {
    question: "Can I attend a free demo class first?",
    answer:
      "Yes. Submit the short demo form and the QA Coder team will follow up with demo timing and course guidance.",
  },
];

const courseOptions = [
  "Complete QA Career Program",
  "Manual Testing Foundation",
  "Selenium Automation with Java",
  "API Testing with Postman and Rest Assured",
  "Playwright Automation Testing",
  "Not sure - need guidance",
];

const experienceOptions = [
  "Fresher / Student",
  "Non-IT background",
  "Manual tester",
  "Automation tester",
  "Working professional",
  "Career switcher",
];

export default function EnrollPage() {
  const [openFAQ, setOpenFAQ] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    experienceLevel: "",
    courseInterest: "",
  });

  const scrollToSection = (id) => {
    const section = document.getElementById(id);
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleCtaClick = ({ label, location, targetSection }) => {
    trackCtaClick({
      label,
      location,
      targetPath: targetSection,
      sourcePath: window.location.hash || window.location.pathname,
    });
    scrollToSection(targetSection);
  };

  const handleChange = (event) => {
    setFormData((currentData) => ({
      ...currentData,
      [event.target.name]: event.target.value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationError = validateDemoEnquiryForm(formData);

    if (validationError) {
      alert(validationError);
      return;
    }

    setLoading(true);

    try {
      await createEnquiry(
        {
          ...formData,
          name: formData.name.trim(),
          email: formData.email.trim().toLowerCase(),
          phone: formData.phone.trim(),
          enquiryType: "demo",
          message: `Free demo request. Experience: ${formData.experienceLevel}. Interest: ${formData.courseInterest}.`,
        },
        {
          source: "enroll-page",
          enquiryType: "demo",
        }
      );

      trackUserActivity({
        action: "enquiry_submitted",
        sourcePath: window.location.hash || window.location.pathname,
        metadata: {
          courseInterest: formData.courseInterest,
          experienceLevel: formData.experienceLevel,
        },
      });

      trackCourseInterest({
        courseTitle: formData.courseInterest,
        action: "enquiry_submitted",
        sourcePath: window.location.hash || window.location.pathname,
      });

      alert("Enquiry submitted successfully. We will contact you soon.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        experienceLevel: "",
        courseInterest: "",
      });
    } catch (error) {
      console.error("Unable to submit enquiry:", error);
      alert("Something went wrong. Please try again or send your enquiry from the contact page.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="enroll-page">
      <section className="enroll-hero">
        <div className="enroll-container enroll-hero-grid">
          <div className="enroll-hero-content">
            <span className="enroll-eyebrow">Live QA training with project practice</span>
            <h1>Become a job-ready QA Engineer with a guided testing career program.</h1>
            <p>
              Learn Manual Testing, Selenium, Playwright, API Testing, SQL, automation
              frameworks, resume preparation, and mock interviews with mentor support.
            </p>

            <div className="enroll-hero-actions">
              <button
                className="enroll-btn enroll-btn-primary"
                onClick={() =>
                  handleCtaClick({
                    label: "Hero Free Demo",
                    location: "hero",
                    targetSection: "enquire",
                  })
                }
              >
                Book Free Demo Class
              </button>
              <button
                className="enroll-btn enroll-btn-secondary"
                onClick={() =>
                  handleCtaClick({
                    label: "Hero Compare Pricing",
                    location: "hero",
                    targetSection: "pricing",
                  })
                }
              >
                Compare Pricing
              </button>
            </div>

            <div className="enroll-trust-row">
              <div>
                <strong>500+</strong>
                <span>Students trained</span>
              </div>
              <div>
                <strong>100+</strong>
                <span>Interview selections</span>
              </div>
              <div>
                <strong>4.8/5</strong>
                <span>Learner rating</span>
              </div>
            </div>
          </div>

          <div className="enroll-hero-card">
            <div className="batch-card">
              <span className="batch-label">Next live batch</span>
              <h2>Starts 30 April</h2>
              <p>Limited weekend and weekday seats available for live mentoring.</p>
              <div className="batch-alert">
                <strong>8 seats left</strong>
                <span>Free demo open this week</span>
              </div>
              <button
                className="enroll-btn enroll-btn-primary"
                onClick={() =>
                  handleCtaClick({
                    label: "Reserve Demo Seat",
                    location: "hero-batch-card",
                    targetSection: "enquire",
                  })
                }
              >
                Reserve Demo Seat
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="enroll-section enroll-urgency-strip">
        <div className="enroll-container urgency-grid">
          <div>
            <strong>Limited batch size</strong>
            <span>Small groups for better doubt solving</span>
          </div>
          <div>
            <strong>Free demo class</strong>
            <span>Attend before you decide to enroll</span>
          </div>
          <div>
            <strong>Career support</strong>
            <span>Resume, mock interviews, and guidance</span>
          </div>
        </div>
      </section>

      <section className="enroll-section" id="pricing">
        <div className="enroll-container">
          <div className="enroll-section-header">
            <span className="enroll-eyebrow">Compare options</span>
            <h2>Choose the right course path for your current level.</h2>
            <p>Start small with foundations or join the complete career program for full job preparation.</p>
          </div>

          <div className="pricing-table">
            {pricingPlans.map((plan) => (
              <article className={`pricing-plan ${plan.featured ? "is-featured" : ""}`} key={plan.name}>
                {plan.featured && <span className="plan-badge">Recommended</span>}
                <h3>{plan.name}</h3>
                <p>{plan.idealFor}</p>
                <strong>{plan.price}</strong>
                <span>{plan.duration}</span>
                <ul>
                  {plan.features.map((feature) => (
                    <li key={feature}>{feature}</li>
                  ))}
                </ul>
                <button
                  className="enroll-btn enroll-btn-primary"
                  onClick={() =>
                    handleCtaClick({
                      label: `Book Demo - ${plan.name}`,
                      location: "pricing-table",
                      targetSection: "enquire",
                    })
                  }
                >
                  Book Demo
                </button>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="enroll-section course-policy-section" aria-label="Course policy documents">
        <div className="enroll-container course-policy-card">
          <div>
            <span className="enroll-eyebrow">Course documents</span>
            <h2>Review the important policies before joining.</h2>
            <p>
              We keep course expectations transparent. You can read the privacy, terms,
              and refund documents anytime before booking a demo or enrolling.
            </p>
          </div>
          <div className="course-policy-links">
            <NavLink to="/refund-policy">Refund Policy</NavLink>
            <NavLink to="/terms">Terms & Conditions</NavLink>
            <NavLink to="/privacy-policy">Privacy Policy</NavLink>
          </div>
        </div>
      </section>

      <section className="enroll-section enroll-split-section">
        <div className="enroll-container split-grid">
          <div>
            <span className="enroll-eyebrow">Who is this for?</span>
            <h2>Built for learners who want a clear QA career path.</h2>
            <p>
              The course is structured so beginners, non-IT learners, and working testers can
              follow a practical path without confusion.
            </p>
          </div>
          <div className="pill-grid">
            {learnerProfiles.map((item) => (
              <span key={item}>{item}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="enroll-section">
        <div className="enroll-container">
          <div className="enroll-section-header">
            <span className="enroll-eyebrow">What you will build</span>
            <h2>Real project output you can explain in interviews.</h2>
          </div>

          <div className="build-grid">
            {buildItems.map((item, index) => (
              <div className="build-card" key={item}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <p>{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="enroll-section outcome-section">
        <div className="enroll-container">
          <div className="outcome-grid">
            {outcomes.map((item) => (
              <div className="outcome-card" key={item.label}>
                <strong>{item.value}</strong>
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="courses_out" className="enroll-section">
        <div className="enroll-container">
          <div className="enroll-section-header">
            <span className="enroll-eyebrow">Top courses</span>
            <h2>Focused training modules for every QA career stage.</h2>
          </div>

          <div className="courses-grid">
            {courses.map((course) => (
              <article className={`course-card ${course.popular ? "popular" : ""}`} key={course.title}>
                <span className="course-badge">{course.badge}</span>
                <h3>{course.title}</h3>
                <p>{course.desc}</p>
                <div className="course-meta">
                  <span>{course.duration}</span>
                  <span>{course.online} online</span>
                </div>
                <ul>
                  {course.outcomes.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <div className="course-actions">
                  <button
                    className="enroll-btn enroll-btn-secondary"
                    onClick={() => {
                      trackCourseInterest({
                        courseTitle: course.title,
                        action: "view_details",
                        sourcePath: window.location.hash || window.location.pathname,
                      });
                      setSelectedCourse(course);
                    }}
                  >
                    View Details
                  </button>
                  <button
                    className="enroll-btn enroll-btn-primary"
                    onClick={() => {
                      trackCourseInterest({
                        courseTitle: course.title,
                        action: "demo_cta_click",
                        sourcePath: window.location.hash || window.location.pathname,
                      });
                      handleCtaClick({
                        label: `Free Demo - ${course.title}`,
                        location: "course-card",
                        targetSection: "enquire",
                      });
                    }}
                  >
                    Free Demo
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {selectedCourse && (
        <div className="enroll-modal-overlay" onClick={() => setSelectedCourse(null)}>
          <div className="enroll-modal-card" onClick={(event) => event.stopPropagation()}>
            <button className="modal-close-btn" type="button" onClick={() => setSelectedCourse(null)}>
              x
            </button>
            <span className="course-badge">{selectedCourse.badge}</span>
            <h2>{selectedCourse.title}</h2>
            <p>{selectedCourse.details}</p>
            <div className="modal-course-meta">
              <span>Duration: {selectedCourse.duration}</span>
              <span>Online: {selectedCourse.online}</span>
              <span>Offline: {selectedCourse.offline}</span>
            </div>
            <div className="modal-actions">
              <button className="enroll-btn enroll-btn-secondary" onClick={() => setSelectedCourse(null)}>
                Close
              </button>
              <button
                className="enroll-btn enroll-btn-primary"
                onClick={() => {
                  trackCourseInterest({
                    courseTitle: selectedCourse.title,
                    action: "modal_demo_click",
                    sourcePath: window.location.hash || window.location.pathname,
                  });
                  setSelectedCourse(null);
                  handleCtaClick({
                    label: `Book Demo - ${selectedCourse.title}`,
                    location: "course-modal",
                    targetSection: "enquire",
                  });
                }}
              >
                Book Demo
              </button>
            </div>
          </div>
        </div>
      )}

      <section className="enroll-section trainer-section">
        <div className="enroll-container">
          <div className="enroll-section-header">
            <span className="enroll-eyebrow">Mentor-led learning</span>
            <h2>Learn from a Lead Engineer who teaches real QA delivery.</h2>
            <p>
              The course is not only theory. You learn how QA work happens in projects:
              planning, testing, automation, defects, CI/CD, reporting, and interview explanation.
            </p>
          </div>

          <div className="trainer-lead-card">
            <div className="trainer-brand-panel" aria-label="QA Coder mentor visual">
              <img src={qaCoderLogo} alt="QA Coder training logo" />
              <div className="trainer-brand-orbit">
                <span>Manual</span>
                <span>API</span>
                <span>Automation</span>
                <span>CI/CD</span>
                {/* <span>Frameworks</span> */}
{/* 
                <span>Real projects</span>
                <span>Resume prep</span>
                <span>Mock interviews</span> */}
                
              </div>
            </div>

            <div className="trainer-profile-copy">
              <span className="trainer-role-badge">QA Coder training team</span>
              <h3>Learn with Manish and Mithlesh.</h3>
              <p>
                QA Coder training is led by mentors who focus on practical QA thinking,
                automation frameworks, API validation, CI/CD workflows, defect analysis,
                and interview-ready project confidence.
              </p>

              <div className="trainer-highlight-grid">
                {mentorHighlights.map((highlight) => (
                  <div key={highlight}>
                    <span>✓</span>
                    <p>{highlight}</p>
                  </div>
                ))}
              </div>

              <div className="trainer-tool-stack" aria-label="Trainer tools and technologies">
                {mentorTools.map((tool) => (
                  <span key={tool}>{tool}</span>
                ))}
              </div>
            </div>
          </div>

          <div className="trainer-support-grid" aria-label="QA Coder trainers">
            {trainerProfiles.map((trainer) => (
              <article className="trainer-support-card" key={trainer.name}>
                <div className="trainer-support-avatar">{trainer.initials}</div>
                <div>
                  <span className="trainer-support-role">{trainer.role}</span>
                  <h3>{trainer.name}</h3>
                  <p>{trainer.description}</p>
                  <div className="trainer-support-tags">
                    {trainer.focus.map((item) => (
                      <span key={item}>{item}</span>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="enroll-section">
        <div className="enroll-container">
          <div className="enroll-section-header">
            <span className="enroll-eyebrow">Success stories</span>
            <h2>Learners who improved their interview confidence.</h2>
            <p>These examples show the type of outcomes our training is designed to support.</p>
          </div>

          <div className="success-grid">
            {successStories.map((story) => (
              <article className="success-card" key={story.name}>
                <div>
                  <strong>{story.name}</strong>
                  <span>{story.result}</span>
                </div>
                <p>"{story.quote}"</p>
                <small>{story.company}</small>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="enquire" className="enroll-section enquiry-section">
        <div className="enroll-container enquiry-grid">
          <div className="enquiry-copy">
            <span className="enroll-eyebrow">Free demo class</span>
            <h2>Share your details. We will suggest the right QA learning path.</h2>
            <p>
              The form is intentionally short. Submit it and our team will contact you with
              demo timing, course guidance, and next batch details.
            </p>
            <div className="enquiry-highlight">
              <strong>Response time:</strong>
              <span>Usually within 2 working hours</span>
            </div>
          </div>

          <form className="enquiry-form-card" onSubmit={handleSubmit}>
            <div className="form-field">
              <label htmlFor="name">Name *</label>
              <input
                id="name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your full name"
                required
              />
            </div>

            <div className="form-field">
              <label htmlFor="email">Email *</label>
              <input
                id="email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                required
              />
            </div>

            <div className="form-field">
              <label htmlFor="phone">Phone *</label>
              <input
                id="phone"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Your phone number"
                required
              />
            </div>

            <div className="form-field">
              <label htmlFor="experienceLevel">Experience Level *</label>
              <select
                id="experienceLevel"
                name="experienceLevel"
                value={formData.experienceLevel}
                onChange={handleChange}
                required
              >
                <option value="">Select your level</option>
                {experienceOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label htmlFor="courseInterest">Course Interest *</label>
              <select
                id="courseInterest"
                name="courseInterest"
                value={formData.courseInterest}
                onChange={handleChange}
                required
              >
                <option value="">Select course interest</option>
                {courseOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <button className="enroll-submit-btn" type="submit" disabled={loading}>
              {loading ? "Submitting..." : "Book My Free Demo"}
            </button>

            <NavLink className="form-contact-link" to="/contact">
              Have questions first? Send a course enquiry
            </NavLink>

            <p className="form-policy-note">
              By submitting this demo request, you agree to our{" "}
              <NavLink to="/terms" target="_blank" rel="noopener noreferrer">
                Terms
              </NavLink>
              ,{" "}
              <NavLink to="/privacy-policy" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </NavLink>
              , and{" "}
              <NavLink to="/refund-policy" target="_blank" rel="noopener noreferrer">
                Refund Policy
              </NavLink>
              .
            </p>
          </form>
        </div>
      </section>

      <section className="enroll-section faq-section">
        <div className="enroll-container">
          <div className="enroll-section-header">
            <span className="enroll-eyebrow">Common questions</span>
            <h2>Answers to the questions learners usually ask before joining.</h2>
          </div>

          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div className={`faq-item ${openFAQ === index ? "open" : ""}`} key={faq.question}>
                <button type="button" onClick={() => setOpenFAQ(openFAQ === index ? null : index)}>
                  <span>{faq.question}</span>
                  <strong>{openFAQ === index ? "-" : "+"}</strong>
                </button>
                {openFAQ === index && <p>{faq.answer}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="enroll-final-cta">
        <div className="enroll-container final-cta-card">
          <div>
            <span className="enroll-eyebrow">Ready to start?</span>
            <h2>Book your free demo and choose your QA career path with confidence.</h2>
          </div>
          <div className="final-cta-actions">
            <button
              className="enroll-btn enroll-btn-primary"
              onClick={() =>
                handleCtaClick({
                  label: "Final CTA Free Demo",
                  location: "final-cta",
                  targetSection: "enquire",
                })
              }
            >
              Book Free Demo
            </button>
            <NavLink className="enroll-btn enroll-btn-secondary" to="/automation-testing">
              Try Free Practice
            </NavLink>
          </div>
        </div>
      </section>
    </main>
  );
}
